import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { ArrowRight, Eye, EyeSlash, Globe } from '@phosphor-icons/react';
import { BACKEND_ORIGIN } from '../config';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      toast.success(t('welcomeBack') + '!');
      navigate('/offers');
    } catch (err) {
      let errorMsg =
        formatApiErrorDetail(err.response?.data?.detail) || err.message;
      if (!err.response && (err.code === "ERR_NETWORK" || err.message === "Network Error")) {
        errorMsg = `Cannot reach the API at ${BACKEND_ORIGIN}. The API may be offline — on the server, check that uvicorn/PM2 is running and nginx proxies to port 8000.`;
      }
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Language Switcher - Top Right */}
      <div className="absolute top-6 right-6 z-20">
        <button
          onClick={() => setLanguage(language === 'en' ? 'nl' : 'en')}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 text-white hover:bg-white/20 transition-colors"
          data-testid="login-language-switcher"
        >
          <Globe size={18} />
          <span className="text-sm font-medium">{language === 'en' ? 'NL' : 'EN'}</span>
        </button>
      </div>

      {/* Left side - Branding */}
      <div className="login-left">
        <div className="mb-8">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00CBA9] to-[#00A88C] flex items-center justify-center">
              <span className="text-white font-bold text-xl">V</span>
            </div>
            <span className="text-white text-2xl font-bold">VMK</span>
          </div>
          <h1 className="text-5xl font-bold text-white mb-4 leading-tight">
            {t('loginHeroLine1')}<br />
            <span className="text-gradient">{t('loginHeroLine2')}</span>
          </h1>
          <p className="text-[#94a3b8] text-lg max-w-md">
            {t('loginHeroSubtitle')}
          </p>
        </div>

        <div className="flex items-center gap-8 mt-12">
          <div className="text-center">
            <div className="text-3xl font-bold text-white">500+</div>
            <div className="text-sm text-[#64748b]">{t('deliveriesMonth')}</div>
          </div>
          <div className="w-px h-12 bg-[#334155]"></div>
          <div className="text-center">
            <div className="text-3xl font-bold text-white">15+</div>
            <div className="text-sm text-[#64748b]">{t('activeTeams')}</div>
          </div>
          <div className="w-px h-12 bg-[#334155]"></div>
          <div className="text-center">
            <div className="text-3xl font-bold text-white">98%</div>
            <div className="text-sm text-[#64748b]">{t('onTimeRate')}</div>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="login-right">
        <div className="login-box">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-[#1a1a1a] mb-2">{t('welcomeBack')}</h2>
            <p className="text-[#64748b]">{t('enterCredentials')}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-5">
              <label className="block text-sm font-medium text-[#1a1a1a] mb-2">{t('email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="vmk-input"
                placeholder={t('emailPlaceholder')}
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1a1a1a] mb-2">{t('password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="vmk-input pr-12"
                  placeholder={t('passwordPlaceholder')}
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#1a1a1a] transition-colors"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="vmk-btn vmk-btn-primary w-full h-12 text-base"
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  {t('signIn')}
                  <ArrowRight size={18} weight="bold" />
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#e2e8f0] text-center">
            <p className="text-xs text-[#94a3b8]">
              Thermo-Solutions Monteurs • VMK Planning System
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
